import React, { useState } from 'react';
import { Button, Col, Dropdown, Row, Select, Modal } from 'antd';
import { FireOutlined, DownOutlined, UpOutlined, CloseOutlined, FormOutlined, PlusOutlined, AppstoreOutlined, BarChartOutlined, ApartmentOutlined } from '@ant-design/icons';
import Logo from '../../assets/images/single-logo.svg'
import FireIcon from '../../assets/images/fire-icon.png'
import Editicon from '../../assets/images/edit.svg'
import UploadIcon from '../../assets/images/upload-contant-icon.svg'

const { Option } = Select;

const MarkdownLinkText = ({ text }) => {
  if (!text) return null;

  // Items can arrive as objects ({ title, url }) or numbers, not just strings —
  // coerce to a string first so .replace never throws.
  let raw = text;
  if (typeof raw !== "string") {
    if (typeof raw === "object") {
      raw = raw.url ? `[${raw.title || ""}](${raw.url})` : (raw.title || "");
    } else {
      raw = String(raw);
    }
  }
  if (!raw) return null;

  const cleanedText = raw.replace(/^\d+[\.\)]?\s*/, "").trim();

  // 1️⃣ Proper markdown link
  const properMatch = cleanedText.match(/\[(.*?)\]\((.*?)\)/);
  if (properMatch) {
    return (
      <a
        href={properMatch[2]}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#1890ff",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        {properMatch[1]}
      </a>
    );
  }

  // 2️⃣ Broken markdown (missing closing bracket)
  const brokenMatch = cleanedText.match(/\[(.*?)\]\((.*)/);
  if (brokenMatch) {
    return (
      <a
        href={brokenMatch[2]}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#1890ff",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        {brokenMatch[1]}
      </a>
    );
  }

  // 3️⃣ Raw URL inside text
  const urlMatch = cleanedText.match(/https?:\/\/[^\s]+/);
  if (urlMatch) {
    return (
      <a
        href={urlMatch[0]}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#1890ff",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        {cleanedText.replace(urlMatch[0], "").trim() || urlMatch[0]}
      </a>
    );
  }

  // 4️⃣ Plain text — no embedded URL at all (common when this item was
  // topped up from the AI's text output rather than the news scrape). Every
  // headline/topic/question should still be a real, clickable link, so fall
  // back to a live web search of the exact text rather than leaving it as
  // unclickable plain text.
  return (
    <a
      href={`https://www.google.com/search?q=${encodeURIComponent(cleanedText)}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#1890ff", textDecoration: "none", fontWeight: 500 }}
    >
      {cleanedText}
    </a>
  );
};

// Content ideas ask the model for a trailing angle tag, e.g.
// "How AI is reshaping X [Data-Driven]" — split it off so it can render as
// its own badge instead of trailing text, and so Write/copy actions get the
// clean idea text.
const ANGLE_TAGS = ["Contrarian", "Data-Driven", "Story-Led", "How-To", "Trend-Spotting", "Personal Take"];
const splitAngleTag = (text) => {
  let str = text;
  if (typeof str !== "string") {
    str = typeof str === "object" && str ? (str.title || "") : String(str || "");
  }
  const m = str.match(/\[([^\]]+)\]\s*$/);
  if (m && ANGLE_TAGS.includes(m[1].trim())) {
    return { label: str.slice(0, m.index).trim(), tag: m[1].trim() };
  }
  return { label: str, tag: null };
};

const CardsView = ({ expanded, toggleExpand, cardData, onWrite }) => {
  if (!cardData.length) {
    return (
      <div className="no-data-message">
        Please enter a topic and run research.
      </div>
    );
  }
  return (
  <div className="cards-grid">
    {cardData.map((item, index) => (
      <div className="content-card" key={index}>
        <div className="content-idea-card">
          <h6><MarkdownLinkText text={item.keyword} /></h6>
          <p className="card-sub">Keyword</p>
          <div className="card-top-icon">
            <img src={Logo} alt="fly" />
          </div>
          <Button
            size="small"
            type="primary"
            className="write-idea-btn"
            icon={<FormOutlined />}
            onClick={() => onWrite && onWrite(item.keyword)}
            title="Write with Assistant"
          >
            Write
          </Button>
        </div>
        <div className="content-idea-card">
          <h6>Search volume</h6>
          <p>{item.volume}</p>
        </div>
        <div className="content-idea-card">
          <h6>Subtopic bullets</h6>
          <ul className="bullets">
            {item.bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
        <div className="content-idea-card">
          <div className="fire-headding">
            <h6 className="trend-label">Trending Icon</h6>
            <img src={FireIcon} />
          </div>
          {expanded[index] && (
            <ul className="bullets">
              {item.trendWords.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </div>
        <Button className="show-more-btn" onClick={() => toggleExpand(index)}>
          {expanded[index] ? "Show less" : "Show more"}{" "}
          {expanded[index] ? <UpOutlined /> : <DownOutlined />}
        </Button>
      </div>
    ))}
  </div>
  );
};
const Overview = ({ contentHeadlines = [], contentQuestions = [], onWrite }) => (
  <div className="owercards-grid">
    <Row gutter={[12, 12]}>
      <Col span={24} lg={12}>
        <div className='owerview-cards'>
          <h6 className='ow-headding'>TOP 10 content Ideas </h6>
          <ul className="owerview-listing">
            {contentHeadlines.length > 0 ? (
              contentHeadlines.map((h, i) => {
                const { label, tag } = splitAngleTag(h);
                return (
                <li key={i} className="owerview-card-item">
                  <span style={{ marginRight: 4 }}>{i + 1}.</span>
                  <MarkdownLinkText text={label} />
                  {tag && <span className={`angle-tag angle-tag-${tag.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{tag}</span>}
                  <Button
                    size="small"
                    type="text"
                    className="write-idea-btn"
                    icon={<FormOutlined />}
                    onClick={() => onWrite && onWrite(label)}
                    title="Write with Assistant"
                  />
                </li>
                );
              })
            ) : (
              <li className="no-data-message">
                Please enter a topic and run research.
              </li>
            )}
          </ul>
        </div>
      </Col>
      <Col span={24} lg={12}>
        <div className='owerview-cards'>
          <h6 className='ow-headding'> Top 10 Interesting Questions </h6>
          <ul className="owerview-listing">
            {contentQuestions.length > 0 ? (
              contentQuestions.map((h, i) => (
                <li key={i}>
                  <span style={{ marginRight: 4 }}>{i + 1}.</span>
                  <MarkdownLinkText text={h} />
                  <Button
                    size="small"
                    type="text"
                    className="write-idea-btn"
                    icon={<FormOutlined />}
                    onClick={() => onWrite && onWrite(h)}
                    title="Write with Assistant"
                  />
                </li>
              ))
            ) : (
              <li className="no-data-message">
                Please enter a topic and run research.
              </li>
            )}
          </ul>
        </div>
      </Col>
    </Row>
  </div>
);

// ---- Dynamic, interactive radial mind map (SVG) ------------------------
// Renders the live research data instead of a static PNG: topKeyword sits in
// the centre, relatedHeadlines fan out as coloured branches, and
// relatedQuestions are spread across those branches as leaf nodes.
// Every node is clickable — selecting one highlights it and opens a detail
// panel with a "Write about this" action.
const MM_SIZE = 560;
const MM_CX = MM_SIZE / 2;
const MM_CY = MM_SIZE / 2;
// Branch colours — kept on the app palette (theme green/blue + accents).
const MM_PALETTE = ["#36B37E", "#F2A23C", "#E5687B", "#6C5CE7", "#1FB6A8", "#4F8FD6"];

const mmCleanLabel = (text) => {
  if (!text) return "";
  // Items can arrive as objects ({ title, url }), not just markdown strings —
  // coerce the same way MarkdownLinkText does so this never renders
  // "[object Object]" in the mind map.
  let raw = text;
  if (typeof raw !== "string") {
    raw = typeof raw === "object" ? (raw.title || "") : String(raw);
  }
  let s = raw.replace(/^\d+[\.\)]?\s*/, "").trim();
  const m = s.match(/\[(.*?)\]\((.*?)\)/); // strip markdown link -> use label
  if (m) s = m[1];
  return s.replace(/[#*`]/g, "").trim();
};

const mmPolar = (cx, cy, r, angleDeg) => {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

// Greedy word-wrap into at most `maxLines` lines of ~`maxChars`, ellipsising
// any overflow so long headlines stay readable inside a circle.
const mmWrap = (text, maxChars, maxLines) => {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  let i = 0;
  for (; i < words.length; i++) {
    const tryLine = cur ? `${cur} ${words[i]}` : words[i];
    if (tryLine.length <= maxChars || !cur) {
      cur = tryLine;
    } else {
      lines.push(cur);
      cur = words[i];
      if (lines.length === maxLines) break;
    }
  }
  if (lines.length < maxLines && cur) {
    lines.push(cur);
    cur = "";
    i++;
  }
  const usedAll = i >= words.length && !cur;
  let out = lines
    .slice(0, maxLines)
    .map((l) => (l.length > maxChars ? `${l.slice(0, maxChars - 1).trimEnd()}…` : l));
  if (!usedAll && out.length && !out[out.length - 1].endsWith("…")) {
    let last = out[out.length - 1];
    last = (last.length > maxChars - 1 ? last.slice(0, maxChars - 1).trimEnd() : last) + "…";
    out[out.length - 1] = last;
  }
  return out.length ? out : [""];
};

// Centred multi-line SVG text built from <tspan> rows.
const MmText = ({ x, y, lines, fontSize, fill, weight }) => {
  const lineH = fontSize * 1.15;
  const startDy = -((lines.length - 1) / 2) * lineH;
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={fontSize}
      fontWeight={weight}
      fill={fill}
      style={{ pointerEvents: "none" }}
    >
      {lines.map((ln, i) => (
        <tspan key={i} x={x} dy={i === 0 ? startDy : lineH}>
          {ln}
        </tspan>
      ))}
    </text>
  );
};

const DynamicMindmap = ({ relatedHeadlines = [], relatedQuestions = [], topKeyword, onWrite }) => {
  const [selected, setSelected] = useState(null);

  const centerFull = mmCleanLabel(topKeyword) || "Topic";

  const headlineItems = relatedHeadlines.map(mmCleanLabel).filter(Boolean);
  const questionItems = relatedQuestions.map(mmCleanLabel).filter(Boolean);

  const fromHeadlines = headlineItems.length > 0;
  const branchItems = (fromHeadlines ? headlineItems : questionItems).slice(0, 6);

  if (branchItems.length === 0) {
    return (
      <div className="no-data-message">
        Please enter a topic and run research.
      </div>
    );
  }

  const n = branchItems.length;
  const branchR = 165;
  const leafR = 76;
  const step = 360 / n;

  const branches = branchItems.map((label, i) => {
    const angle = -90 + i * step; // start at top, go clockwise
    return {
      id: `branch-${i}`,
      label,
      angle,
      color: MM_PALETTE[i % MM_PALETTE.length],
      pos: mmPolar(MM_CX, MM_CY, branchR, angle),
    };
  });

  // Spread questions across branches (max 2 each) as leaves — only when the
  // branches came from headlines, otherwise questions are already the branches.
  const leavesByBranch = branches.map(() => []);
  if (fromHeadlines) {
    let qi = 0;
    for (let round = 0; round < 2; round++) {
      for (let b = 0; b < n && qi < questionItems.length; b++) {
        leavesByBranch[b].push(questionItems[qi++]);
      }
    }
  }

  const isSel = (id) => selected?.id === id;
  const pick = (node) =>
    setSelected((prev) => (prev?.id === node.id ? null : node));

  let delayIdx = 0;
  const nextDelay = () => `${(delayIdx++) * 0.05}s`;

  return (
    <div className="mindmap-canvas">
      <svg
        viewBox={`0 0 ${MM_SIZE} ${MM_SIZE}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        className="dynamic-mindmap-svg"
        role="img"
        aria-label={`Mind map for ${centerFull}`}
      >
        {/* connectors: center -> branch */}
        {branches.map((b, i) => (
          <line
            key={`bl-${i}`}
            x1={MM_CX}
            y1={MM_CY}
            x2={b.pos.x}
            y2={b.pos.y}
            stroke={isSel(b.id) ? b.color : "#cbd5e1"}
            strokeWidth={isSel(b.id) ? 3 : 2}
          />
        ))}

        {/* connectors + leaf nodes */}
        {branches.map((b, i) =>
          leavesByBranch[i].map((leaf, j) => {
            const L = leavesByBranch[i].length;
            const leafAngle = b.angle + (j - (L - 1) / 2) * 24;
            const lp = mmPolar(b.pos.x, b.pos.y, leafR, leafAngle);
            const id = `leaf-${i}-${j}`;
            const node = { id, label: leaf, color: b.color };
            return (
              <g key={id}>
                <line
                  x1={b.pos.x}
                  y1={b.pos.y}
                  x2={lp.x}
                  y2={lp.y}
                  stroke={isSel(id) ? b.color : "#e2e8f0"}
                  strokeWidth="1.5"
                />
                <g
                  className={`mm-node mm-leaf${isSel(id) ? " mm-selected" : ""}`}
                  style={{ animationDelay: nextDelay() }}
                  onClick={() => pick(node)}
                >
                  <title>{leaf}</title>
                  {isSel(id) && (
                    <circle cx={lp.x} cy={lp.y} r="29" fill="none" stroke={b.color} strokeWidth="2" opacity="0.5" />
                  )}
                  <circle cx={lp.x} cy={lp.y} r="25" fill="#ffffff" stroke={b.color} strokeWidth="1.5" />
                  <MmText x={lp.x} y={lp.y} lines={mmWrap(leaf, 9, 2)} fontSize={8.5} fill="#475569" weight="500" />
                </g>
              </g>
            );
          })
        )}

        {/* branch nodes */}
        {branches.map((b) => (
          <g
            key={b.id}
            className={`mm-node mm-branch${isSel(b.id) ? " mm-selected" : ""}`}
            style={{ animationDelay: nextDelay() }}
            onClick={() => pick(b)}
          >
            <title>{b.label}</title>
            {isSel(b.id) && (
              <circle cx={b.pos.x} cy={b.pos.y} r="46" fill="none" stroke={b.color} strokeWidth="2.5" opacity="0.5" />
            )}
            <circle cx={b.pos.x} cy={b.pos.y} r="40" fill={b.color} />
            <MmText x={b.pos.x} y={b.pos.y} lines={mmWrap(b.label, 9, 2)} fontSize={11} fill="#ffffff" weight="600" />
          </g>
        ))}

        {/* center node */}
        <g
          className={`mm-node mm-center${isSel("center") ? " mm-selected" : ""}`}
          onClick={() => pick({ id: "center", label: centerFull, color: "#3B6B94" })}
        >
          <title>{centerFull}</title>
          {isSel("center") && (
            <circle cx={MM_CX} cy={MM_CY} r="59" fill="none" stroke="#3B6B94" strokeWidth="2.5" opacity="0.5" />
          )}
          <circle cx={MM_CX} cy={MM_CY} r="52" fill="#3B6B94" />
          <MmText x={MM_CX} y={MM_CY} lines={mmWrap(centerFull, 10, 2)} fontSize={13} fill="#ffffff" weight="700" />
        </g>
      </svg>

      {selected && (
        <div className="mindmap-detail">
          <span className="mm-detail-dot" style={{ background: selected.color }} />
          <span className="mm-detail-label" title={selected.label}>
            {selected.label}
          </span>
          <div className="mm-detail-actions">
            <Button
              size="small"
              type="primary"
              icon={<FormOutlined />}
              onClick={() => onWrite && onWrite(selected.label)}
            >
              Write
            </Button>
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setSelected(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Mindmap = ({ relatedHeadlines = [], relatedQuestions = [], topKeyword, onWrite }) => (
  <div className="mind-map-grid">
    <Row gutter={[5, 12]}>
      <Col span={24} lg={10}>
        <div className='mind-map-image'>
          <DynamicMindmap
            relatedHeadlines={relatedHeadlines}
            relatedQuestions={relatedQuestions}
            topKeyword={topKeyword}
            onWrite={onWrite}
          />
        </div>
      </Col>
      <Col span={24} lg={14}>
        <div className='mindcard-sec-cards'>

          <h3 className='ow-headding'>Todays top keyword : {<MarkdownLinkText text={topKeyword}/>} </h3>
          <h6 className='ow-headding'>HEADLINES </h6>
          <ul className="owerview-listing">
            {relatedHeadlines.length > 0 ? (
              relatedHeadlines.map((h, i) => (
                <li key={i}>
                  <span style={{ marginRight: 4 }}>{i + 1}.</span>
                  <MarkdownLinkText text={h} />
                </li>
              ))
            ) : (
              <li className="no-data-message">
                Please enter a topic and run research.
              </li>
            )}
          </ul>

          <div className='listing-mindmap'>
            <h6 className='ow-headding'>Questions</h6>
          </div>
          <div className='listing-mindmap'>
            <ul className="owerview-listing">
              {relatedQuestions.length > 0 ? (
                relatedQuestions.map((h, i) => (
                  <li key={i}>
                    <span style={{ marginRight: 4 }}>{i + 1}.</span>
                    <MarkdownLinkText text={h} />
                  </li>
                ))
              ) : (
                <li className="no-data-message">
                  Please enter a topic and run research.
                </li>
              )}
            </ul>
          </div>
        </div>
      </Col>
    </Row>
  </div>
);

const ContentIdeas = ({ contentHeadlines = [], contentQuestions = [], relatedQuestions = [], relatedHeadlines = [], topKeyword = "", cardData = [], onLoadMore, loadingMore = false, onWrite, ...props }) => {

  const [activeView, setActiveView] = useState('cards');
  const [expanded, setExpanded] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState('');

  // Try to get the topic from props if available
  const researchTopic = props.topic || props.researchTopic || '';

  const toggleExpand = (index) => {
    setExpanded(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleChange = (value) => {
    console.log(`Selected: ${value}`);
  };

  const handleCreateAIContent = () => {
    setIsModalOpen(true);
  };

  const handleModalOk = () => {
    if (selectedIdea) {
      setIsModalOpen(false);
      if (onWrite) onWrite(selectedIdea);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedIdea('');
  };

  const viewIcons = {
    cards: <AppstoreOutlined className="tab-icon" />,
    overview: <BarChartOutlined className="tab-icon" />,
    mindmap: <ApartmentOutlined className="tab-icon" />,
  };

  return (
    <div className="content-ideas-container">
      <div className="topbar">
        <div className="view-buttons">
          {['cards', 'overview', 'mindmap'].map(view => (
            <Button
              key={view}
              className={`tab-btn-content ${activeView === view ? 'active' : ''}`}
              onClick={() => setActiveView(view)}
            >
              {viewIcons[view]}
              {view.charAt(0).toUpperCase() + view.slice(1)}
            </Button>
          ))}
        </div>
        <div className="top-actions">
          <Button className='create-contant-btn' onClick={handleCreateAIContent}><img src={Editicon} />Create AI content</Button>
        </div>
      </div>

      <Modal
        title="Select a Content Idea"
        className='tab_modal'
        open={isModalOpen}
        width={600}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okButtonProps={{ disabled: !selectedIdea }}
      >
        <div>
      {contentHeadlines && contentHeadlines.length > 0 ? (
  <ul className="main-new-list-classh" style={{ listStyle: "none", padding: 0, margin: 0 }}>
    {contentHeadlines.map((idea, idx) => (
      <li
        key={idx}
        className={selectedIdea === idea ? "selected-idea" : "unselected-idea"}
        onClick={() => setSelectedIdea(idea)}
        style={{
          marginBottom: "8px",
          cursor: "pointer",
          padding: "6px 10px",
          borderRadius: "6px",
        }}
      >
        {idea}
      </li>
    ))}
  </ul>
) : (
  <div className="no-content-message" style={{ padding: "10px", color: "#666" }}>
    No content ideas available.
  </div>
)}

          {onLoadMore && (
            <div style={{ textAlign: "center", marginTop: 14 }}>
              <Button
                className="load-more-ideas-btn"
                loading={loadingMore}
                onClick={onLoadMore}
                icon={!loadingMore ? <PlusOutlined /> : null}
              >
                {loadingMore ? "Generating more ideas…" : "Get more ideas"}
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <div className="content-area-outer">
        {activeView === 'cards' && <CardsView expanded={expanded} toggleExpand={toggleExpand} cardData={cardData} onWrite={onWrite} />}
        {activeView === 'overview' && <Overview contentHeadlines={contentHeadlines} contentQuestions={contentQuestions} onWrite={onWrite} />}
        {activeView === 'mindmap' && <Mindmap relatedHeadlines={relatedHeadlines} relatedQuestions={relatedQuestions} topKeyword={topKeyword} onWrite={onWrite} />}
      </div>
    </div>
  );
};

export default ContentIdeas;
